<template>
  <div class="p-8">
    <div class="mx-auto max-w-5xl space-y-6">
      <div class="flex justify-start items-center gap-1">
        <svg
          @click="router.go(-1)"
          class="h-8 w-8 text-blue-600 hover:text-blue-700 cursor-pointer"
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M11 15l-3-3m0 0l3-3m-3 3h8M3 12a9 9 0 1118 0 9 9 0 01-18 0z"
          />
        </svg>
        <h1 class="text-3xl font-semibold ">Email Job Completed</h1>
      </div>

      <div v-if="isLoading" class="flex justify-center gap-1">
        <div class="spinner dark"></div>
        <div>Loading</div>
      </div>

      <form v-else class="max-w-3xl space-y-5" @submit.prevent="sendMail">
        <div>
          <label
            for="email_from"
            class="block text-sm font-medium text-gray-600"
          >
            From
          </label>
          <input
            type="text"
            v-model="business.settings_default_email_from"
            disabled
            name="email_from"
            class="w-full px-4 py-2 rounded-r"
          />
        </div>

        <div>
          <label for="email_to" class="block text-sm font-medium text-gray-600">
            To
          </label>
          <input
            type="text"
            v-model="job.customer.customer_email"
            name="email_to"
            disabled
            class="w-full px-4 py-2 rounded-r"
          />
        </div>

        <div>
          <label for="email_cc" class="block text-sm font-medium text-gray-600">
            CC
          </label>
          <div class="flex justify-between">
            <input
              @change="checkEmails()"
              type="text"
              v-model.trim="cc"
              name="email_cc"
              class="w-full px-4 py-2 rounded-r"
            />
          </div>
        </div>

        <div>
          <label
            for="email_template"
            class="block text-sm font-medium text-gray-600"
          >
            Email Content
          </label>
          <textarea
            v-model="business.job_email_template"
            name="email_template"
            class="mt-1 w-full rounded px-4 py-2"
            rows="8"
          ></textarea>
        </div>

        <div class="pt-2 flex justify-center">
          <button
            type="submit"
            class="bg-blue-600 submit-btn text-white text-2xl font-bold w-60 rounded py-2 flex justify-center gap-2"
          >
            <div v-if="isPending" class="spinner"></div>
            Send
          </button>
        </div>

        <div
          v-if="error || emailError || email_err"
          class="text-center text-red-600 font-semibold"
        >
          {{ error }} {{ emailError }} {{ email_err }}
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { ref, watch } from "vue";
import { useRoute, useRouter } from "vue-router";
import { functions, timestamp } from "@/firebaseConfig";
import getDocument from "@/composables/getDocument";
import useCollection from "@/composables/useCollection";

export default {
  props: ["job_id", "user"],

  setup(props) {
    const route = useRoute();
    const router = useRouter();
    const parent = route.path.substring(0, route.path.lastIndexOf("/"));

    const isLoading = ref(true);
    const isPending = ref(false);
    const emailsHasError = ref(false);
    const cc = ref("");
    const emailError = ref("");

    const recipient = ref([]);

    const business_id = props.user.claims.business_id ?? props.id;

    const { error, document: job } = getDocument(
      `businesses/${business_id}/jobs`,
      props.job_id
    );

    const { error: business_error, document: business } = getDocument(
      `businesses`,
      business_id
    );

    const { error: email_err, addDoc: email_addDoc } = useCollection(
      `businesses/${business_id}/emails`
    );

    watch(job, () => {
      if (job.value && !job.value.job_completed) {
        router.push(parent);
      }
      if (job.value) {
        isLoading.value = false;
      }
    });

    const sendMail = async () => {
      if (emailsHasError.value) return;
      isPending.value = true;
      try {
        const sendingEmail = functions.httpsCallable("job_sendMail");
        const data = {
          from: business.value?.settings_default_email_from,
          to: job.value?.customer?.customer_email,
          cc: cc.value,
          body: business.value?.job_email_template,
          mailgun_api_key: business.value.mailgun_api_key,
          mailgun_domain: business.value.mailgun_domain,
        };
        const sendHasErrorEmail = await sendingEmail(data);

        if (!sendHasErrorEmail.data) {
          email_addDoc({
            createdAt: timestamp(),
            createdBy: props.user.uid,
            email_type: "send_job_completed",
            email_from: business.value?.settings_default_email_from,
            email_to: job.value?.customer?.customer_email,
            email_cc: cc.value.trim() ?? "",
            email_content: business.value?.job_email_template,
          });
        }

        isPending.value = false;
        router.push(parent);
      } catch (e) {
        isPending.value = false;
        return e;
      }
    };

    const checkEmails = () => {
      if (!cc.value) {
        emailError.value = "";
        return;
      }
      const emails = cc.value.split(",");
      emailsHasError.value = false;
      emailError.value = "";
      emails.map((email) => {
        if (!validateEmail(email.trim())) {
          emailsHasError.value = true;
          emailError.value =
            "Please enter valid email address for the CC field.";
        }
      });
    };

    const validateEmail = (email) => {
      const reg = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
      return reg.test(email);
    };

    return {
      router,
      error,
      cc,
      isLoading,
      isPending,
      business,
      emailError,
      sendMail,
      checkEmails,
      recipient,
      business_error,
      emailsHasError,
      email_err,
      job,
    };
  },
};
</script>
